import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import Player from 'lottie-react';
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";
import theme from "../../util/theme/theme";
import axios from 'axios';
import Cookies from 'js-cookie';
import okAnimation from '../../files/json/approved.json';
import searchingAnimation from '../../files/json/searching.json';
import { StepLineIndicator } from "../ui/step-indicator";
import { useToast } from "../../providers/toaster-provider";
import { FileDetails } from "../../pages/transactions";
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import TreeDropdown, { TreeDropdownOption } from '../tree-dropdown';
import { X } from 'lucide-react';
import { useUser } from "../../providers/user-provider";

interface CategoryProvider {
  id: number;
  name: string;
  country?: string;
  importFormat?: string;
}

interface SelectFileStepProps {
  file: File | null;
  errors: { file?: string };
  loading?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (file: File | null) => void;
  formatFileSize: (size: number) => string;
  providers: CategoryProvider[];
  selectedProviderId: number | null;
  onProviderChange: (id: number | null) => void;
}

const SelectFileStep: React.FC<SelectFileStepProps> = ({
  file,
  errors,
  loading,
  fileInputRef,
  onFileChange,
  formatFileSize,
  providers,
  selectedProviderId,
  onProviderChange,
}) => {
  const providerOptions: DropdownOption[] = providers.map((provider) => ({
    value: String(provider.id),
    label: provider.country 
      ? `${provider.name} (${provider.country})`
      : provider.name,
  }));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <Box mb={2} sx={{ width: '100%', maxWidth: 600 }}>
        <Typography fontSize={14} color="var(--color-text-muted)" mb={1}>
          Category Provider <span style={{ color: '#ff3b3b' }}>*</span>
        </Typography>
        <Dropdown
          options={providerOptions}
          value={selectedProviderId ? String(selectedProviderId) : ''}
          onChange={(value) => onProviderChange(value ? Number(value) : null)}
          placeholder="Select a provider..."
          height={40}
          isPrimary={!!selectedProviderId}
        />
      </Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={1} sx={{ width: '100%', maxWidth: 600 }}>
        Choose a File <span style={{ color: '#ff3b3b' }}>*</span>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--color-container-background)',
          borderRadius: 1,
          border: `1px solid ${errors.file ? '#e31616ff' : '#e0e0e0'}`,
          px: 2,
          py: 1.2,
          width: '100%',
          maxWidth: 600,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: file ? 'inherit' : '#b0b0b0',
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file
            ? `${file.name} (${formatFileSize(file.size)})`
            : 'CSV, TSV formats, up to 50MB'}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const selectedFile = e.target.files?.[0] || null;
            onFileChange(selectedFile);
            if (e.target) {
              e.target.value = '';
            }
          }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={{
            ml: 2,
            borderRadius: 2,
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 600,
          }}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          Upload
        </Button>
      </Box>
      {errors.file && (
        <Box mt={0.5}>
          <Typography fontSize={12} color="#dc1111ff" pl={2}>
            {errors.file}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface DuplicateCheckStepProps {
  isProcessing: boolean;
  duplicates: any[];
  fileDetails?: FileDetails | null;
  duplicateCheckProgress?: { currentChunk: number; totalChunks: number; processedRows: number; totalRows: number };
}

const DuplicateCheckStep: React.FC<DuplicateCheckStepProps> = ({
  isProcessing,
  duplicates,
  fileDetails,
  duplicateCheckProgress = { currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 },
}) => {
  const columns: MRT_ColumnDef<Record<string, any>>[] = (fileDetails?.headers || []).map(header => ({
    accessorKey: header,
    header: header,
    size: 150,
  }));

  return (
    <Box>
      {isProcessing ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={searchingAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
            <Box 
              sx={{ 
                height: 8, 
                borderRadius: 4, 
                background: '#e0e0e0',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  height: '100%', 
                  background: 'var(--color-primary)',
                  width: `${duplicateCheckProgress.totalRows > 0 ? (duplicateCheckProgress.processedRows / duplicateCheckProgress.totalRows) * 100 : 0}%`,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
              <Typography variant="body2" color="text.secondary">
                Checking for duplicates...
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {duplicateCheckProgress.processedRows} / {duplicateCheckProgress.totalRows} entries analyzed
            </Typography>
          </Box>
        </Box>
      ) : duplicates.length === 0 ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={okAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: 'var(--color-primary)' }}>
            No duplicates found
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box 
            sx={{ 
              color: 'var(--color-primary)',
              padding: 0,
              textAlign: 'center',
              minHeight: 75,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--color-container-background)'
            }}
          >
            {!duplicates.length &&
              <Player
                autoplay
                loop
                animationData={okAnimation}
                style={{ height: 160, width: 160 }}
              />
            }
            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
              Duplicate entry(s) has been found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              {duplicates.length} {duplicates.length === 1 ? 'entry' : 'entries'} were removed because they already exist in the system or in the import file.
            </Typography>
          </Box>
          <Box position="relative" sx={{ mt: 1 }}>
            <MaterialReactTable
              columns={columns}
              data={duplicates as Record<string, any>[]}
              enableRowVirtualization
              enableColumnVirtualization
              rowVirtualizerOptions={{ overscan: 20 }}
              columnVirtualizerOptions={{ overscan: 2 }}
              enablePagination={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableColumnActions={false}
              initialState={{ density: 'compact' }}
              muiTablePaperProps={{
                sx: { boxShadow: 'none' },
              }}
              muiTableContainerProps={{
                sx: { maxHeight: 260, background: 'var(--color-container-background)' },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface AssignParentStepProps {
  fileDetails?: FileDetails | null;
  approvedParentSuggestions: Set<string>;
  onApproveParent: (rowId: string, approved: boolean) => void;
  onApproveAllParents: (rowIds: Set<string>, approved: boolean) => void;
  selectedRowIds: Set<string>;
  duplicates: any[];
}

const AssignParentStep: React.FC<AssignParentStepProps> = ({ 
  fileDetails, 
  approvedParentSuggestions, 
  onApproveParent, 
  onApproveAllParents,
  selectedRowIds,
  duplicates
}) => {
  // Filter to only show selected rows (excluding duplicates)
  const duplicateRowIds = new Set(duplicates.map((dup: any) => (dup as any).__rowId));
  const rows = (fileDetails?.parsedRows || []).filter((row: any) => 
    selectedRowIds.has((row as any).__rowId) && !duplicateRowIds.has((row as any).__rowId)
  );
  
  // Find the code column (case-insensitive, ignore *)
  const codeColumnName = rows.length > 0 
    ? (Object.keys(rows[0]).find((key: string) => key.replace(/\*/g, '').toLowerCase() === 'code') || 'Code')
    : 'Code';
  
  const nameColumnName = rows.length > 0 
    ? (Object.keys(rows[0]).find((key: string) => key.replace(/\*/g, '').toLowerCase() === 'name') || 'Name')
    : 'Name';
  
  const codeRows = rows.filter((row: any) => row[codeColumnName as string]?.trim()).map((row: any) => ({ 
    ...row, 
    [codeColumnName as string]: (row[codeColumnName as string] || '').trim() 
  }));
  
  const allCodes = codeRows.map((row: any) => (row[codeColumnName as string] || '').trim()).sort();

  // Find parent candidates for each code
  const getParentCandidates = (code: string) => {
    const trimmedCode = code.trim();
    const candidates = allCodes
      .filter(c => c !== trimmedCode && trimmedCode.startsWith(c) && c.length < trimmedCode.length)
      .sort((a, b) => b.length - a.length);
    return candidates;
  };

  // Filter to only show rows with parent suggestions
  const rowsWithParents = codeRows.filter((row: any) => {
    const code = (row[codeColumnName as string] || '').trim();
    return getParentCandidates(code).length > 0;
  });

  const allRowIds = new Set(rowsWithParents.map((row: any) => (row as any).__rowId));
  const allSelected = allRowIds.size > 0 && Array.from(allRowIds).every(id => approvedParentSuggestions.has(id));
  const someSelected = Array.from(allRowIds).some(id => approvedParentSuggestions.has(id));

  // Auto-approve all parents when they load for the first time (initial empty state means first time)
  React.useEffect(() => {
    if (allRowIds.size > 0 && approvedParentSuggestions.size === 0) {
      onApproveAllParents(allRowIds, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: MRT_ColumnDef<Record<string, any>>[] = [
    {
      id: 'approve',
      header: 'Approve',
      size: 70,
      Cell: ({ row }: { row: any }) => {
        const rowId = (row.original as any).__rowId;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={approvedParentSuggestions.has(rowId)}
              onChange={() => onApproveParent(rowId, !approvedParentSuggestions.has(rowId))}
              size="small"
              sx={{ width: 20, height: 12.5 }}
            />
          </Box>
        );
      },
      Header: () => (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => onApproveAllParents(allRowIds, !allSelected)}
          size="small"
          sx={{ width: 20, height: 12.5 }}
        />
      ),
    } as any,
    {
      accessorKey: codeColumnName,
      header: codeColumnName,
      size: 120,
    },
    {
      accessorKey: nameColumnName,
      header: nameColumnName,
      size: 200,
    },
    {
      id: 'suggestedParent',
      header: 'Suggested Parent',
      size: 150,
      Cell: ({ row }: { row: any }) => {
        const code = (row.original[codeColumnName as string] || '').trim();
        const parentCandidates = getParentCandidates(code);
        return parentCandidates.length > 0 ? parentCandidates[0] : '-';
      },
    },
  ];

  return (
    <Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={2}>
        Assign parent categories based on code hierarchy
      </Typography>
      {rowsWithParents.length === 0 ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={okAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: 'var(--color-primary)' }}>
            No codes with parent suggestions
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={rowsWithParents as Record<string, any>[]}
          enableRowVirtualization
          enableColumnVirtualization
          rowVirtualizerOptions={{ overscan: 20 }}
          columnVirtualizerOptions={{ overscan: 2 }}
          enablePagination={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          initialState={{ density: 'compact' }}
          muiTableContainerProps={{ sx: { maxHeight: 300, overflowY: 'auto', position: 'relative' } }}
          muiTablePaperProps={{
            sx: { boxShadow: 'none', background: 'var(--color-container-background)' },
          }}
        />
      )}
    </Box>
  );
};

interface EditDataStepProps {
  fileDetails?: FileDetails | null;
  selectedRowIds: Set<string>;
  onRowSelect: (rowId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onRowChange: (rowId: string, columnKey: string, value: string) => void;
  parentCodeOptions: TreeDropdownOption[];
  missingFieldRows: Set<string>;
}

interface ReviewStepProps {
  fileDetails?: FileDetails | null;
  selectedRowIds: Set<string>;
  duplicates?: any[];
}

const EditDataStep: React.FC<EditDataStepProps> = ({ 
  fileDetails, 
  selectedRowIds,
  onRowSelect,
  onSelectAll,
  onRowChange, 
  parentCodeOptions,
  missingFieldRows,
}) => {
  const allRowIds = new Set(fileDetails?.parsedRows?.map((row: any) => (row as any).__rowId) || []);
  const allSelected = allRowIds.size > 0 && Array.from(allRowIds).every(id => selectedRowIds.has(id));
  const someSelected = Array.from(allRowIds).some(id => selectedRowIds.has(id));

  const columns: MRT_ColumnDef<Record<string, any>>[] = [
    {
      id: 'select',
      header: 'Select',
      size: 50,
      Cell: ({ row }: { row: any }) => {
        const rowId = (row.original as any).__rowId;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={selectedRowIds.has(rowId)}
              onChange={(e) => onRowSelect(rowId, e.target.checked)}
              indeterminate={false}
              size="small"
              sx={{ width: 20, height: 12.5 }}
            />
          </Box>
        );
      },
      Header: () => (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
          size="small"
          sx={{ width: 20, height: 12.5 }}
        />
      ),
    } as any,
    ...(fileDetails?.headers || []).map(header => ({
      accessorKey: header,
      header: header,
      size: 150,
      enableEditing: true,
      Cell: ({ row }: { row: any }) => {
        const rowId = (row.original as any).__rowId;
        const value = row.original[header];
        
        if (header === 'parentCode') {
          return (
            <TreeDropdown
              options={parentCodeOptions}
              value={value || null}
              onChange={(selectedId: string | null) => onRowChange(rowId, 'parentCode', selectedId || '')}
              placeholder="Select parent code"
              width="100%"
              height={25}
            />
          );
        }
        return (
          <TextField
            size="small"
            value={value || ''}
            onChange={(e) => onRowChange(rowId, header, e.target.value)}
            error={missingFieldRows.has(`${rowId}-${header}`)}
            sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.5 } }}
          />
        );
      },
    })),
  ];

  return (
    <MaterialReactTable
      columns={columns}
      data={fileDetails?.parsedRows as Record<string, any>[] || []}
      enableRowVirtualization
      enableColumnVirtualization
      rowVirtualizerOptions={{ overscan: 20 }}
      columnVirtualizerOptions={{ overscan: 2 }}
      enablePagination={false}
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      initialState={{ density: 'compact' }}
      muiTableContainerProps={{ sx: { maxHeight: 350, overflowY: 'auto', position: 'relative' } }}
      muiTablePaperProps={{
        sx: { boxShadow: 'none' },
      }}
    />
  );
};

const ReviewStep: React.FC<ReviewStepProps> = ({ fileDetails, selectedRowIds, duplicates = [] }) => {
  const duplicateRowIds = new Set(duplicates.map((dup: any) => (dup as any).__rowId));
  const selectedRows = (fileDetails?.parsedRows || [])
    .filter((row: any) => selectedRowIds.has((row as any).__rowId) && !duplicateRowIds.has((row as any).__rowId));
  const headers = fileDetails?.headers || [];
  
  if (selectedRows.length === 0) {
    return (
      <Box 
        sx={{ 
          color: 'var(--color-primary)',
          padding: 4,
          textAlign: 'center',
          minHeight: 75,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-container-background)'
        }}
      >
        <Player
          autoplay
          loop
          animationData={searchingAnimation}
          style={{ height: 160, width: 160 }}
        />
        <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
          No categories selected to import
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={2}>
        Ready to import {selectedRows.length} categor{selectedRows.length !== 1 ? 'ies' : 'y'}
      </Typography>
      <MaterialReactTable
        columns={headers.map(header => ({
          accessorKey: header,
          header: header,
          size: 150,
        })) as MRT_ColumnDef<Record<string, any>>[] }
        data={selectedRows as Record<string, any>[]}
        enableRowVirtualization
        enableColumnVirtualization
        rowVirtualizerOptions={{ overscan: 20 }}
        columnVirtualizerOptions={{ overscan: 2 }}
        enablePagination={false}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnActions={false}
        initialState={{ density: 'compact' }}
        muiTableContainerProps={{ sx: { maxHeight: 300, overflowY: 'auto', position: 'relative' } }}
        muiTablePaperProps={{
          sx: { boxShadow: 'none', background: 'var(--color-container-background)' },
        }}
      />
    </Box>
  );
};

interface ImportProgressStepProps {
  isProcessing: boolean;
  currentChunk: number;
  totalChunks: number;
  processedCategories: number;
  totalCategories: number;
  status: 'processing' | 'success' | 'error';
  importedEntries: any[];
  fileDetails?: FileDetails | null;
}

const ImportProgressStep: React.FC<ImportProgressStepProps> = ({ 
  isProcessing, 
  currentChunk, 
  totalChunks, 
  processedCategories, 
  totalCategories,
  status,
  importedEntries,
  fileDetails 
}) => {
  const headers = fileDetails?.headers || [];

  if (isProcessing) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={3}>
        <CircularProgress size={48} sx={{ color: 'var(--color-primary)' }} />
        <Box sx={{ width: '80%', maxWidth: 400 }}>
          <Box 
            sx={{ 
              height: 8, 
              borderRadius: 4, 
              background: '#e0e0e0',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                height: '100%', 
                background: 'var(--color-primary)',
                width: `${(processedCategories / totalCategories) * 100}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Importing categories... {currentChunk} of {totalChunks}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Processed: {processedCategories} / {totalCategories} categories
        </Typography>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box position="relative">
        {importedEntries.length === 0 ? (
          <Box 
            sx={{ 
              border: '1px solid #FFD700',
              borderRadius: 2,
              backgroundColor: 'rgba(255, 217, 0, 0.13)',
              color: '#B8860B',
              padding: 4,
              textAlign: 'center',
              minHeight: 75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
              No categories were imported
            </Typography>
          </Box>
        ) : (
          <>
            <Typography fontSize={14} color="var(--color-text-muted)" mb={2}>
              Successfully imported {importedEntries.length} categor{importedEntries.length !== 1 ? 'ies' : 'y'}
            </Typography>
            <MaterialReactTable
              columns={[
                {
                  accessorKey: 'code',
                  header: 'Code',
                  size: 120,
                },
                {
                  accessorKey: 'name',
                  header: 'Name',
                  size: 200,
                },
              ] as MRT_ColumnDef<Record<string, any>>[]}
              data={importedEntries as Record<string, any>[]}
              enableRowVirtualization
              enableColumnVirtualization
              rowVirtualizerOptions={{ overscan: 20 }}
              columnVirtualizerOptions={{ overscan: 2 }}
              enablePagination={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableColumnActions={false}
              initialState={{ density: 'compact' }}
              muiTableContainerProps={{ sx: { maxHeight: 300, overflowY: 'auto', position: 'relative' } }}
              muiTablePaperProps={{
                sx: { boxShadow: 'none', background: 'var(--color-container-background)' },
              }}
            />
          </>
        )}
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        border: '1px solid #e31616ff',
        borderRadius: 2,
        backgroundColor: 'rgba(227, 22, 22, 0.1)',
        color: '#e31616ff',
        padding: 4,
        textAlign: 'center',
        minHeight: 75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
        Import failed. Please try again.
      </Typography>
    </Box>
  );
};

export interface ImportCategoryPopupProps {
  open: boolean;
  fileDetails?: FileDetails | null;
  onFileChange: (details: FileDetails) => void;
  onProviderChange: (value: number | null) => void;
  onImport: (selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => void;
  onImportProgress?: (state: { currentChunk: number; totalChunks: number; processedCategories: number; totalCategories: number; status: 'processing' | 'success' | 'error'; importedEntries: any[] }) => void;
  onEnterEditDataStep?: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ImportCategoryPopup: React.FC<ImportCategoryPopupProps> = ({
  open,
  fileDetails,
  onFileChange,
  onProviderChange,
  onImport,
  onImportProgress,
  onEnterEditDataStep,
  onCancel,
  loading = false,
}) => {
  const { showError } = useToast();
  const { user } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState<{ file?: string }>({});
  const [duplicates, setDuplicates] = React.useState<any[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [providers, setProviders] = React.useState<CategoryProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = React.useState<number | null>(null);
  const [fetchProviders, setFetchProviders] = useApi();
  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<string>>(new Set());
  const [approvedParentSuggestions, setApprovedParentSuggestions] = React.useState<Set<string>>(new Set());
  const [missingFieldRows, setMissingFieldRows] = React.useState<Set<string>>(new Set());
  const initializedFileRef = React.useRef<any>(null);
  const enteredEditDataStepRef = React.useRef(false);
  const processedDuplicatesRef = React.useRef<string>('');
  const [importProgressModal, setImportProgressModal] = React.useState({
    currentChunk: 0,
    totalChunks: 0,
    processedCategories: 0,
    totalCategories: 0,
    status: 'processing' as 'processing' | 'success' | 'error'
  });
  const [importedEntries, setImportedEntries] = React.useState<any[]>([]);
  const [hasProcessedStep, setHasProcessedStep] = React.useState<boolean[]>([false, false, false, false, false, false]);
  const [dupCheckCached, setDupCheckCached] = React.useState(false);
  const [duplicateCheckProgress, setDuplicateCheckProgress] = React.useState<{ currentChunk: number; totalChunks: number; processedRows: number; totalRows: number }>({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
  const initializedParentApprovalsRef = React.useRef<boolean>(false);

  // Initialize selection when file details load (default all selected)
  React.useEffect(() => {
    if (fileDetails?.parsedRows && initializedFileRef.current !== fileDetails.parsedRows) {
      const allIds = new Set((fileDetails.parsedRows as any[]).map(row => (row as any).__rowId));
      setSelectedRowIds(allIds);
      initializedFileRef.current = fileDetails.parsedRows;
      // Clear dup check cache when new file is selected
      setDupCheckCached(false);
      setDuplicates([]);
      // Reset parent approval initialization when new file is selected
      initializedParentApprovalsRef.current = false;
    }
  }, [fileDetails?.parsedRows]);

  React.useEffect(() => {
    if (!open) {
      fileInputRef.current = null;
      setFile(null);
      setErrors({});
      setStep(0);
      setDuplicates([]);
      setSelectedProviderId(null);
      setSelectedRowIds(new Set());
      setApprovedParentSuggestions(new Set());
      initializedFileRef.current = null;
      initializedParentApprovalsRef.current = false;
      setHasProcessedStep([false, false, false, false, false, false]);
      setDupCheckCached(false);
      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
    } else {
      // Fetch providers when modal opens
      const { url, method } = buildApiRequestConfig(routes.categoryProviders.fetch, [{ param: 'includeAllActive', value: true }]);
      setFetchProviders({ url, method });
    }
  }, [open, setFetchProviders]);

  // Handle providers fetch response
  React.useEffect(() => {
    const { data, isLoading, error } = fetchProviders;
    if (data && !isLoading) {
      const formattedProviders = data.map((prov: any) => ({
        id: prov.id,
        name: prov.name,
        country: prov.country,
        importFormat: prov.importFormat,
      }));
      setProviders(formattedProviders);
    }
    if (error) {
      showError("Failed to fetch category providers");
    }
  }, [fetchProviders, showError]);

  // Check duplicates via API
  const checkDuplicatesAPI = React.useCallback(async (parsedRows: any[], codeColumnName: string, nameColumnName: string) => {
    if (!user?.entityId || !parsedRows.length) {
      return;
    }

    try {
      const CHUNK_SIZE = 50;
      const chunks = [];
      const allFoundDuplicates: any[] = [];
      const nonDuplicateRows: any[] = [];

      // Create chunks from parsed rows
      for (let i = 0; i < parsedRows.length; i += CHUNK_SIZE) {
        chunks.push(parsedRows.slice(i, i + CHUNK_SIZE));
      }

      // Initialize progress
      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: chunks.length, processedRows: 0, totalRows: parsedRows.length });

      // Process chunks sequentially
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        try {
          const chunk = chunks[chunkIndex];
          const { url, method } = buildApiRequestConfig(routes.categories.checkDuplicate, [{ param: 'entityId', value: user.entityId }]);

          const requestData = chunk.map(item => ({
            code: typeof item === 'object' && item !== null ? (item as any)[codeColumnName] : '',
            name: typeof item === 'object' && item !== null ? (item as any)[nameColumnName] : ''
          }));

          const token = Cookies.get('token');
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await axios({
            method,
            url,
            headers,
            data: requestData
          });

          const apiDuplicates = (response.data?.duplicates || []) as any[];
          
          // Mark rows that are duplicates
          const foundDuplicateSet = new Set<string>();
          apiDuplicates.forEach((dup: any) => {
            // Handle nested structure where category is inside the duplicate object
            const code = (dup.category?.code || dup.code || '').trim().toLowerCase();
            const name = (dup.category?.name || dup.name || '').trim().toLowerCase();
            const key = `${code}|${name}`;
            foundDuplicateSet.add(key);
          });

          // Separate duplicates from non-duplicates
          chunk.forEach((row: any) => {
            const code = (row[codeColumnName] || '').trim().toLowerCase();
            const name = (row[nameColumnName] || '').trim().toLowerCase();
            const key = `${code}|${name}`;
            
            if (foundDuplicateSet.has(key)) {
              allFoundDuplicates.push(row);
            } else {
              nonDuplicateRows.push(row);
            }
          });

          // Update progress
          setDuplicateCheckProgress(prev => ({
            ...prev,
            currentChunk: chunkIndex + 1,
            processedRows: Math.min((chunkIndex + 1) * CHUNK_SIZE, parsedRows.length)
          }));

          // Small delay between chunks
          if (chunkIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (error) {
          console.error('Error checking duplicates in chunk:', chunkIndex, error);
          // Continue with next chunk even if one fails
          chunks[chunkIndex].forEach((row: any) => {
            nonDuplicateRows.push(row);
          });
        }
      }

      setDuplicates(allFoundDuplicates);
    } catch (err: any) {
      console.error('Error during duplicate check:', err);
      setDuplicates([]);
    } finally {
      setIsProcessing(false);
      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
    }
  }, [user?.entityId]);

  // Auto-deselect detected duplicates (don't remove them from fileDetails)
  React.useEffect(() => {
    if (duplicates.length > 0) {
      // Create a unique key for this set of duplicates to prevent reprocessing
      const duplicateKey = duplicates.map((d: any) => (d as any).__rowId).join(',');
      
      // Only process if we haven't already processed this exact set
      if (processedDuplicatesRef.current !== duplicateKey) {
        processedDuplicatesRef.current = duplicateKey;
        
        const duplicateRowIds = new Set(duplicates.map((dup: any) => (dup as any).__rowId));
        
        // Remove duplicates from selectedRowIds only
        setSelectedRowIds(prevIds => {
          const updated = new Set(prevIds);
          duplicateRowIds.forEach(id => updated.delete(id));
          return updated;
        });
      }
    }
  }, [duplicates]);

  // Parse file using the provider's importFormat
  React.useEffect(() => {
    if (!file) return;
    // NOTE: Provider check disabled for now - files can be parsed without a provider

    const readFileAsText = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(f, "utf-8");
      });

    const detectSeparator = (sample: string): string => {
      const candidates = [",", "\t", ";", "|"];
      const scores = candidates.map((sep) => {
        const lines = sample.split(/\r\n|\n|\r/).slice(0, 5);
        const total = lines.reduce((acc, l) => acc + (l.split(sep).length - 1), 0);
        return { sep, total };
      });
      scores.sort((a, b) => b.total - a.total);
      return scores[0].total > 0 ? scores[0].sep : ",";
    };

    const parseDelimited = (text: string, sep: string): string[][] => {
      const rows: string[][] = [];
      let cur = "";
      let row: string[] = [];
      let inQuotes = false;

      for (let i = 0; i < text.length; i++) {
        const ch = text[i];

        if (inQuotes) {
          if (ch === '"') {
            if (text[i + 1] === '"') {
              cur += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            cur += ch;
          }
        } else {
          if (ch === '"') {
            inQuotes = true;
          } else if (ch === sep) {
            row.push(cur);
            cur = "";
          } else if (ch === '\r') {
            // ignore
          } else if (ch === '\n') {
            row.push(cur);
            rows.push(row);
            row = [];
            cur = "";
          } else {
            cur += ch;
          }
        }
      }

      if (cur !== "" || row.length > 0) {
        row.push(cur);
        rows.push(row);
      }

      return rows;
    };

    (async () => {
      try {
        const name = file.name || '';
        const ext = name.split('.').pop()?.toLowerCase();
        if (!['csv', 'tsv', 'txt'].includes(ext || '')) {
          showError('Only CSV/TSV/TXT text formats are parsed in this UI.');
          return;
        }

        const text = await readFileAsText(file);
        if (!text) {
          showError('No data found in the file.');
          return;
        }

        const sample = text.split(/\r\n|\n|\r/).slice(0, 10).join('\n');
        const sep = ext === 'tsv' ? '\t' : detectSeparator(sample);
        const rows = parseDelimited(text, sep);

        if (!rows.length) {
          showError('No rows parsed from the file.');
          return;
        }

        const headers = rows[0].map((cell, idx) => (cell ?? '').toString().trim() || `column_${idx}`);
        const dataRows = rows.slice(1);

        // Find code and name column indices (case-insensitive, ignore *)
        const codeColIdx = headers.findIndex(h => h.replace(/\*/g, '').toLowerCase() === 'code');
        const nameColIdx = headers.findIndex(h => h.replace(/\*/g, '').toLowerCase() === 'name');

        if (codeColIdx === -1 || nameColIdx === -1) {
          showError('CSV must contain "Code" and "Name" columns.');
          return;
        }

        const codeColumnName = headers[codeColIdx];
        const nameColumnName = headers[nameColIdx];
        const limitedHeaders = [codeColumnName, nameColumnName];

        const parsedRows = dataRows.map((row, idx) => {
          const obj: Record<string, string> = { __rowId: String(idx) };
          obj[codeColumnName] = (row[codeColIdx] ?? '').trim();
          obj[nameColumnName] = (row[nameColIdx] ?? '').trim();
          return obj;
        });

        // Check for duplicates within the file first
        const seenCodes = new Set<string>();
        const internalDuplicates: any[] = [];
        parsedRows.forEach((row) => {
          const code = typeof row === 'object' && row !== null ? ((row as any)[codeColumnName] || '') : '';
          if (seenCodes.has(code)) {
            internalDuplicates.push(row);
          }
          seenCodes.add(code);
        });

        setIsProcessing(false);
        onFileChange({
          file,
          parsedRows: parsedRows as any,
          failedRows: [],
          result: {
            successfulRows: parsedRows.length,
            failedRows: 0,
            totalRows: rows.length,
          },
          headers: limitedHeaders,
        } as FileDetails);
      } catch (err: any) {
        showError(err?.toString?.() ?? 'Failed to parse file');
        setIsProcessing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, onFileChange]);

  // Create import event when entering step 1 (EditDataStep)
  React.useEffect(() => {
    if (step === 1 && !enteredEditDataStepRef.current && onEnterEditDataStep) {
      console.log('Entering step 1 (EditDataStep), creating import event');
      enteredEditDataStepRef.current = true;
      onEnterEditDataStep();
    }
    // Reset ref when exiting step 1
    if (step !== 1) {
      enteredEditDataStepRef.current = false;
    }
  }, [step, onEnterEditDataStep]);

  const validate = (): boolean => {
    const newErrors: { file?: string } = {};
    if (!file) {
      newErrors.file = 'File is required';
    } else {
      if (file.type !== 'text/csv' && file.type !== 'text/tab-separated-values') {
        newErrors.file = 'Invalid file type. Only CSV and TSV files are allowed.';
      }
      if (file.size > 50 * 1024 * 1024) {
        newErrors.file = 'File size exceeds 50MB limit';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatFileSize = (size: number): string => {
    const kb = size / 1024;
    if (kb < 1024) return `${Math.ceil(kb)} KB`;
    return `${Math.ceil(kb / 1024)} MB`;
  };

  const handleNextStep = () => {
    if (step === 0) {
      if (validate()) {
        // Move to step 1 (Edit Data)
        setHasProcessedStep(prev => {
          const updated = [...prev];
          updated[1] = true;
          return updated;
        });
        setStep(1);
      }
    } else if (step === 2) {
      // Moving from duplicate check to assign parents
      // Initialize parent approvals on first entry to this step
      if (!initializedParentApprovalsRef.current) {
        setApprovedParentSuggestions(new Set());  // Clear any stale approvals
        initializedParentApprovalsRef.current = true;
      }
      setHasProcessedStep(prev => {
        const updated = [...prev];
        updated[3] = true;
        return updated;
      });
      setIsProcessing(false);
      setStep(3);
    } else if (step === 1) {
      // Validate selected rows have code and name before moving to duplicate check
      const missingFields = new Set<string>();
      const selectedRows = (fileDetails?.parsedRows || []).filter((row: any) => selectedRowIds.has((row as any).__rowId));
      const codeFieldName = fileDetails?.headers?.[0] || '';
      const nameFieldName = fileDetails?.headers?.[1] || '';
      
      selectedRows.forEach((row: any) => {
        const code = (row[codeFieldName] || '').trim();
        const name = (row[nameFieldName] || '').trim();
        
        if (!code) {
          missingFields.add(`${row.__rowId}-${codeFieldName}`);
        }
        if (!name) {
          missingFields.add(`${row.__rowId}-${nameFieldName}`);
        }
      });
      
      if (missingFields.size > 0) {
        setMissingFieldRows(missingFields);
        showError(`Please fill in missing data before continuing. ${missingFields.size} field(s) are empty.`);
        return;
      }

      // Move to step 2 (Duplicate Check) and run duplicate check if not cached
      setHasProcessedStep(prev => {
        const updated = [...prev];
        updated[2] = true;
        return updated;
      });
      setMissingFieldRows(new Set());
      setStep(2);
      
      // Only run API if not already cached
      if (!dupCheckCached) {
        setIsProcessing(true);
        const codeColumnName = fileDetails?.headers?.[0] || '';
        const nameColumnName = fileDetails?.headers?.[1] || '';
        checkDuplicatesAPI(selectedRows, codeColumnName, nameColumnName);
        setDupCheckCached(true);
      }
    } else if (step === 3) {
      setHasProcessedStep(prev => {
        const updated = [...prev];
        updated[4] = true;
        return updated;
      });
      setStep(4);
    }
  };

  const handlePrevStep = () => {
    if (step > 0) {
      setMissingFieldRows(new Set());
      // Clear duplicate check cache when going back so it re-runs with updated selection
      if (step >= 2) {
        setDupCheckCached(false);
        setDuplicates([]);
      }
      setStep(step - 1);
    }
  };

  const handleRowSelect = (rowId: string, selected: boolean) => {
    const newSelected = new Set(selectedRowIds);
    if (selected) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRowIds(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allIds = new Set((fileDetails?.parsedRows as any[])?.map(row => (row as any).__rowId) || []);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds(new Set());
    }
  };

  const handleApproveParent = (rowId: string, approved: boolean) => {
    const newApproved = new Set(approvedParentSuggestions);
    if (approved) {
      newApproved.add(rowId);
    } else {
      newApproved.delete(rowId);
    }
    setApprovedParentSuggestions(newApproved);
  };

  const handleApproveAllParents = (rowIds: Set<string>, approved: boolean) => {
    if (approved) {
      setApprovedParentSuggestions(new Set(rowIds));
    } else {
      const newApproved = new Set(approvedParentSuggestions);
      rowIds.forEach(id => newApproved.delete(id));
      setApprovedParentSuggestions(newApproved);
    }
  };

  const handleRowChange = (rowId: string, columnKey: string, value: string) => {
    if (!fileDetails?.parsedRows) return;
    
    const updatedRows = fileDetails.parsedRows.map((row: any) => {
      if ((row as any).__rowId === rowId) {
        return { ...row, [columnKey]: value };
      }
      return row;
    });

    onFileChange({
      ...fileDetails,
      parsedRows: updatedRows,
    } as FileDetails);
  };

  const stepRender = [
    <SelectFileStep
      file={file}
      errors={errors}
      loading={loading}
      fileInputRef={fileInputRef}
      onFileChange={setFile}
      formatFileSize={formatFileSize}
      providers={providers}
      selectedProviderId={selectedProviderId}
      onProviderChange={(id) => {
        setSelectedProviderId(id);
        onProviderChange(id);
        setFile(null);
        setErrors({});
      }}
    />,
    <EditDataStep 
      fileDetails={fileDetails}
      selectedRowIds={selectedRowIds}
      onRowSelect={handleRowSelect}
      onSelectAll={handleSelectAll}
      onRowChange={handleRowChange}
      parentCodeOptions={[]}
      missingFieldRows={missingFieldRows}
    />,
    <DuplicateCheckStep
      isProcessing={isProcessing}
      duplicates={duplicates}
      fileDetails={fileDetails}
      duplicateCheckProgress={duplicateCheckProgress}
    />,
    <AssignParentStep
      fileDetails={fileDetails}
      approvedParentSuggestions={approvedParentSuggestions}
      onApproveParent={handleApproveParent}
      onApproveAllParents={handleApproveAllParents}
      selectedRowIds={selectedRowIds}
      duplicates={duplicates}
    />,
    <ReviewStep 
      fileDetails={fileDetails}
      selectedRowIds={selectedRowIds}
      duplicates={duplicates}
    />,
    <ImportProgressStep 
      isProcessing={importProgressModal.status === 'processing'}
      currentChunk={importProgressModal.currentChunk}
      totalChunks={importProgressModal.totalChunks}
      processedCategories={importProgressModal.processedCategories}
      totalCategories={importProgressModal.totalCategories}
      status={importProgressModal.status}
      importedEntries={importedEntries}
      fileDetails={fileDetails}
    />,
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth={isMobile ? false : "md"}
        fullWidth={isMobile}
        {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
        sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
        PaperProps={{
          sx: isMobile
            ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, p: 0, display: 'flex', flexDirection: 'column' }
            : { borderRadius: 4, p: 0, height: 550, display: 'flex', flexDirection: 'column' }
        }}
      >
      <DialogContent sx={{ p: 0, pt: 3, pb: 0, px: isMobile ? 2 : 4, width: isMobile ? '100%' : 800, overflowX: 'hidden', overflowY: 'auto', position: 'relative', flex: 1 }}>
        <IconButton
          onClick={onCancel}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            width: 32,
            height: 32,
          }}
        >
          <X size={18} />
        </IconButton>
        <>
          {fileDetails?.result?.totalRows ? (
            <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>
              There are <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.totalRows}</span> lines in the uploaded file, with <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.successfulRows}</span> entries identified.
            </Typography>
          ):
          (
            <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>Select a file to start the import process.</Typography>
          )
          }
          <Box display="flex" justifyContent="center" mb={5}>
            <StepLineIndicator
              steps={6}
              activeStep={step}
              width={isMobile ? '100%' : 800}
              stepNames={['Select File', 'Edit Data', 'Duplicate Check', 'Assign Parents', 'Review', 'Import']}
              forceCompletedSteps={(() => {
                const completed = [];
                // Mark duplicate check step (now at index 2) as completed if no duplicates found and step was processed
                if (!isProcessing && duplicates.length === 0 && hasProcessedStep[2]) {
                  completed.push(2);
                }
                // Mark import step as completed when import is done
                if (importProgressModal.status === 'success' || importProgressModal.status === 'error') {
                  completed.push(5);
                }
                return completed;
              })()}
            />
          </Box>
        </>

        {stepRender[step]}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: "space-between", gap: 2, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
        {step > 0 && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: 'var(--color-primary)', 
              height: 35, 
              background: 'var(--color-page-background)', 
              border: '1px solid #00b59e' 
            }}
            onClick={handlePrevStep}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step < 4 && (
          <Button
            variant="contained"
            color="primary"
            disabled={loading || (step === 0 && !file) || (step === 2 && isProcessing)}
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: '#fff', 
              height: 35,
              background: loading || (step === 0 && !file) || (step === 2 && isProcessing) ? '#ccc' : 'var(--color-primary)',
            }}
            onClick={handleNextStep}
          >
            {loading || (step === 2 && isProcessing) ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} sx={{ color: '#fff' }} />
                <span>{step === 2 ? 'Checking...' : 'Processing...'}</span>
              </Box>
            ) : (
              'Next'
            )}
          </Button>
        )}
        {step === 4 && (
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: '#fff', 
              height: 35,
              background: loading ? '#ccc' : 'var(--color-primary)',
            }}
            onClick={() => {
              setImportProgressModal({
                currentChunk: 0,
                totalChunks: 0,
                processedCategories: 0,
                totalCategories: (fileDetails?.parsedRows || []).filter((row: any) => selectedRowIds.has((row as any).__rowId)).length,
                status: 'processing'
              });
              setImportedEntries([]);
              setStep(5);
              
              // Get column names
              const headers = fileDetails?.headers || [];
              const codeColumnName = headers[0] || 'Code';
              
              // Create a Set of duplicate row IDs for fast lookup
              const duplicateRowIds = new Set(duplicates.map((dup: any) => (dup as any).__rowId));
              
              // Get selected rows (excluding duplicates) and merge in parent codes
              const selectedRows = (fileDetails?.parsedRows || [])
                .filter((row: any) => selectedRowIds.has((row as any).__rowId) && !duplicateRowIds.has((row as any).__rowId))
                .map((row: any) => {
                  const code = (row[codeColumnName] || '').trim();
                  const rowId = (row as any).__rowId;
                  
                  // Find parent candidates (using same logic as AssignParentStep)
                  const allCodes = (fileDetails?.parsedRows || [])
                    .map((r: any) => ((r[codeColumnName] || '').trim()))
                    .filter(Boolean)
                    .sort();
                  
                  const parentCandidates = allCodes
                    .filter((c: string) => c !== code && code.startsWith(c) && c.length < code.length)
                    .sort((a: string, b: string) => b.length - a.length);
                  
                  // If this row has approved parent, use the best candidate; otherwise null
                  const parentCode = approvedParentSuggestions.has(rowId) && parentCandidates.length > 0 
                    ? parentCandidates[0] 
                    : null;
                  
                  return {
                    ...row,
                    parentCode
                  };
                });
              
              // Create a progress callback that updates component state
              const progressCallback = (state: any) => {
                setImportProgressModal(state);
                setImportedEntries(state.importedEntries || []);
                if (onImportProgress) {
                  onImportProgress(state);
                }
              };
              
              onImport(selectedRows, selectedRowIds, progressCallback);
            }}
          >
            Next
          </Button>
        )}
        {step === 5 && (
          <Button
            variant="contained"
            color="primary"
            disabled={importProgressModal.status === 'processing'}
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: '#fff', 
              height: 35,
              background: importProgressModal.status === 'processing' ? '#ccc' : 'var(--color-primary)',
            }}
            onClick={onCancel}
          >
            {importProgressModal.status === 'processing' ? 'Importing...' : 'Close'}
          </Button>
        )}
      </Box>
      </Dialog>
    
    </>
  );
};

export default ImportCategoryPopup;
